/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

// Lets the active page register ephemeral TopBar actions (e.g. month picker on
// Dashboard). Cleared automatically on unmount / navigation.
interface TopBarActionsContextValue {
  actions: ReactNode | null
  setActions: (actions: ReactNode | null) => void
}

const TopBarActionsContext = createContext<TopBarActionsContextValue | null>(
  null
)

export function TopBarActionsProvider({ children }: { children: ReactNode }) {
  const [actions, setActions] = useState<ReactNode | null>(null)
  const value = useMemo(() => ({ actions, setActions }), [actions])
  return (
    <TopBarActionsContext.Provider value={value}>
      {children}
    </TopBarActionsContext.Provider>
  )
}

// Read the registered actions node (consumed by TopBar).
export function useTopBarActionsValue(): ReactNode | null {
  return useContext(TopBarActionsContext)?.actions ?? null
}

// Register TopBar actions for the active route. Pass a memoized node (e.g.
// useMemo(() => <MonthPicker ... />, [period])) so the effect does not loop.
export function useTopBarActions(node: ReactNode) {
  const setActions = useContext(TopBarActionsContext)?.setActions
  useEffect(() => {
    if (!setActions) return
    setActions(node)
    return () => setActions(null)
  }, [setActions, node])
}
